import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { useI18n } from "../i18n/I18nProvider";
import { CONSENT_EVENT, gaId, getConsent, initAnalytics, setConsent, trackPageView } from "../lib/analytics";

/**
 * Cookie notice for Google Analytics: it tells the visitor it's on and lets them reject it (Rechazar /
 * Entendido), shown until they answer, and again from the footer's «Configurar cookies». Only when an
 * Analytics id is configured. Also sends a page view on every route change; gtag's consent state
 * (analytics.ts) decides whether it goes with cookies or as an anonymous ping.
 */
export function CookieBanner() {
  const { t } = useI18n();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(() => Boolean(gaId()) && getConsent() === null);

  useEffect(() => {
    initAnalytics();
    const onChange = () => setOpen(Boolean(gaId()) && getConsent() === null);
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  useEffect(() => {
    // After the page has rendered its title.
    const id = window.setTimeout(() => trackPageView(pathname + search), 0);
    return () => window.clearTimeout(id);
  }, [pathname, search]);

  if (!open) return null;
  // The current page view already went out when the route rendered: no second one on «Entendido».
  const choose = (v: "granted" | "denied") => setConsent(v);
  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label={t("cookies.title")}>
      <p>
        {t("cookies.text")} <Link to="/cookies">{t("cookies.more")}</Link>
      </p>
      <div className="cookie-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={() => choose("denied")}>
          {t("cookies.reject")}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => choose("granted")}>
          {t("cookies.accept")}
        </button>
      </div>
    </div>
  );
}

/** Footer link that reopens the notice to change the choice. */
export function CookieSettingsLink() {
  const { t } = useI18n();
  if (!gaId()) return null;
  return (
    <button type="button" className="btn btn-link footer-cookie-settings" onClick={() => setConsent(null)}>
      {t("cookies.settings")}
    </button>
  );
}
